import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js';
import type { FrameSnapshot } from './types';
import { BASE_BODY_NAME, BODY_GEOMS } from './phantomx';
import { MuJoCoRunner } from './mujoco-runner';
import { getPublicAssetPath } from './public-path';
import { DEFAULT_THEME, SceneTheme } from './themes';

const BODY_MESHES = new Set(['body.STL']);
const FRAME_MESHES = new Set([
  'connect.STL',
  'connect_coll.STL',
  'thigh_l.STL',
  'tibia_l.STL',
]);
const MESH_OFFSETS: Record<string, [number, number, number]> = {
  'connect.STL': [0, -0.0252, 0],
  'connect_coll.STL': [0, -0.0252, 0],
};

const AXIS_CORRECTION = new THREE.Quaternion().setFromEuler(new THREE.Euler(-Math.PI / 2, 0, 0));
const TEMP_POS = new THREE.Vector3();
const TEMP_QUAT = new THREE.Quaternion();

const PHANTOMX_ASSET_BASE = getPublicAssetPath('phantomx/');

interface ViewerOptions {
  runner: MuJoCoRunner;
}

export class PhantomXViewer {
  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private controls: OrbitControls;
  private loader = new STLLoader();
  private geometries = new Map<string, THREE.BufferGeometry>();
  private bodyNodes = new Map<number, THREE.Object3D>();
  private baseBodyId: number | null = null;
  private readyPromise: Promise<void>;
  private resolveReady!: () => void;
  private followTarget = new THREE.Vector3();
  private theme: SceneTheme = DEFAULT_THEME;
  private ground?: THREE.Mesh;
  private axisGroup?: THREE.Group;
  private rafId: number | null = null;
  private needsRender = true;

  constructor(private readonly canvas: HTMLCanvasElement, private readonly options: ViewerOptions) {
    this.theme = options.theme ?? DEFAULT_THEME;
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    this.renderer.setPixelRatio(window.devicePixelRatio);
    // renderer.encoding deprecated in Three r155 -> use colorSpace when available
    if ('outputColorSpace' in this.renderer && (THREE as any).SRGBColorSpace) {
      (this.renderer as any).outputColorSpace = (THREE as any).SRGBColorSpace;
    }
    this.renderer.setClearColor(this.theme.background, 1);

    this.scene = new THREE.Scene();
    this.scene.fog = new THREE.Fog(this.theme.fog, 10, 26);

    this.camera = new THREE.PerspectiveCamera(45, this.aspect, 0.02, 40);
    this.camera.position.set(1.7, 1.4, 2.3);

    this.controls = new OrbitControls(this.camera, this.canvas);
    this.controls.addEventListener('change', this.handleControlsChange);
    this.controls.enableDamping = true;
    this.controls.enablePan = true;
    this.controls.screenSpacePanning = false;
    this.controls.minDistance = 0.3;
    this.controls.maxDistance = 12;
    this.controls.minPolarAngle = 0.05;
    this.controls.maxPolarAngle = Math.PI / 2.05;
    this.controls.target.set(0, 0.2, 0);

    this.setupLighting();
    this.addGround();
    this.addWorldAxes();
    this.handleResize();
    window.addEventListener('resize', this.handleResize);

    this.readyPromise = new Promise((resolve) => {
      this.resolveReady = resolve;
    });
    void this.buildScene();

    this.startRenderLoop();
  }

  whenReady(): Promise<void> {
    return this.readyPromise;
  }

  dispose(): void {
    window.removeEventListener('resize', this.handleResize);
    this.controls.removeEventListener('change', this.handleControlsChange);
    this.controls.dispose();
    this.renderer.dispose();
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  update(snapshot: FrameSnapshot): void {
    if (!this.bodyNodes.size) {
      return;
    }
    this.updateTransforms(snapshot);
    this.needsRender = true;
  }

  private get aspect(): number {
    const { clientWidth, clientHeight } = this.canvas;
    return clientWidth > 0 && clientHeight > 0 ? clientWidth / clientHeight : 1;
  }

  private async buildScene(): Promise<void> {
    await Promise.all(
      Object.entries(BODY_GEOMS).map(async ([bodyName, geoms]) => {
        const bodyId = this.options.runner.getBodyId(bodyName);
        if (bodyId < 0) {
          console.warn(`PhantomX body ${bodyName} not found in MuJoCo model.`);
          return;
        }
        const group = new THREE.Object3D();
        await Promise.all(geoms.map(async (geom) => {
          const mesh = await this.instantiateMesh(geom.mesh);
          mesh.position.copy(convertVector(geom.pos));
          mesh.quaternion.copy(convertQuaternion(geom.quat));
          group.add(mesh);
        }));
        this.scene.add(group);
        this.bodyNodes.set(bodyId, group);
        if (bodyName === BASE_BODY_NAME) {
          this.baseBodyId = bodyId;
        }
      }),
    );
    this.resolveReady();
  }

  private setupLighting(): void {
    const ambient = new THREE.AmbientLight(0x526077, 0.4);
    const hemi = new THREE.HemisphereLight(0xfef6dd, 0x0b160d, 0.9);
    const key = new THREE.DirectionalLight(0xffffff, 1.4);
    key.position.set(4.5, 5, 3.75);
    key.castShadow = true;
    const fill = new THREE.DirectionalLight(0xc3d7ff, 0.35);
    fill.position.set(-4, 2.3, -3);
    this.scene.add(ambient, hemi, key, fill);
  }

  private addGround(): void {
    const texture = createCheckerTexture(this.theme);
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(9, 9);
    texture.anisotropy = 8;

    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(32, 32),
      new THREE.MeshStandardMaterial({ map: texture, roughness: 0.68, metalness: 0.05 }),
    );
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = 0;
    ground.receiveShadow = true;
    this.scene.add(ground);
    this.ground = ground;
  }

  private addWorldAxes(): void {
    if (this.axisGroup) {
      this.scene.remove(this.axisGroup);
    }
    const axisLength = 1.0;
    const axisOffset = 0.0125;
    const origin = new THREE.Vector3(0, axisOffset, 0);
    const axisGroup = new THREE.Group();
    axisGroup.name = 'worldAxes';
    axisGroup.position.copy(origin);

    const xDir = new THREE.Vector3(1, 0, 0);
    const yDir = new THREE.Vector3(0, 0, -1);

    const xArrow = new THREE.ArrowHelper(xDir, new THREE.Vector3(), axisLength, this.theme.axisX, 0.12, 0.05);
    const yArrow = new THREE.ArrowHelper(yDir, new THREE.Vector3(), axisLength, this.theme.axisY, 0.12, 0.05);
    axisGroup.add(xArrow, yArrow);

    const xLabel = createAxisLabel('X', this.theme.axisX);
    xLabel.position.copy(xDir.clone().setLength(axisLength + 0.12));
    const yLabel = createAxisLabel('Y', this.theme.axisY);
    yLabel.position.copy(yDir.clone().setLength(axisLength + 0.12));
    axisGroup.add(xLabel, yLabel);

    this.scene.add(axisGroup);
    this.axisGroup = axisGroup;
  }

  private async instantiateMesh(meshFile: string): Promise<THREE.Mesh> {
    let geometry = this.geometries.get(meshFile);
    if (!geometry) {
      geometry = await this.loadGeometry(meshFile);
      this.geometries.set(meshFile, geometry);
    }
    const overrides = this.getMaterialOverrides(meshFile);
    const material = new THREE.MeshStandardMaterial({
      color: this.getColorForMesh(meshFile),
      metalness: overrides.metalness ?? 0.25,
      roughness: overrides.roughness ?? 0.5,
      side: THREE.DoubleSide,
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.userData.meshFile = meshFile;
    return mesh;
  }

  private async loadGeometry(meshFile: string): Promise<THREE.BufferGeometry> {
    const assetUrl = `${PHANTOMX_ASSET_BASE}${meshFile}`;
    const response = await fetch(assetUrl);
    if (!response.ok) {
      throw new Error(`Unable to load mesh ${meshFile} (${response.status}).`);
    }
    const arrayBuffer = await response.arrayBuffer();
    const geometry = this.loader.parse(arrayBuffer, PHANTOMX_ASSET_BASE);
    geometry.computeVertexNormals();
    return geometry;
  }

  private updateTransforms(snapshot: FrameSnapshot): void {
    this.bodyNodes.forEach((node, bodyId) => {
      const posIdx = bodyId * 3;
      const quatIdx = bodyId * 4;
      node.position.copy(bufferToThreePosition(snapshot.bodyPositions, posIdx));
      node.quaternion.copy(bufferToThreeQuaternion(snapshot.bodyQuats, quatIdx));
    });

    if (this.baseBodyId !== null) {
      const idx = this.baseBodyId * 3;
      this.followTarget.copy(bufferToThreePosition(snapshot.bodyPositions, idx));
      this.controls.target.lerp(this.followTarget, 0.2);
    }
  }

  private handleResize = (): void => {

    const { clientWidth, clientHeight } = this.canvas;
    if (clientWidth === 0 || clientHeight === 0) {
      return;
    }
    this.renderer.setSize(clientWidth, clientHeight, false);
    this.camera.aspect = clientWidth / clientHeight;
    this.camera.updateProjectionMatrix();
    this.needsRender = true;
  };

  setTheme(theme: SceneTheme): void {
    this.theme = theme;
    this.renderer.setClearColor(theme.background, 1);
    if (this.scene.fog) {
      this.scene.fog.color.setHex(theme.fog);
    } else {
      this.scene.fog = new THREE.Fog(theme.fog, 10, 26);
    }
    this.refreshGroundTheme();
    this.addWorldAxes();
    this.updateRobotMaterials();
  }

  private getColorForMesh(meshFile: string): number {
    if (BODY_MESHES.has(meshFile)) {
      return this.theme.robotBody;
    }
    if (FRAME_MESHES.has(meshFile)) {
      return this.theme.robotFrame;
    }
    return this.theme.robotFrame;
  }

  private getMaterialOverrides(meshFile: string): Partial<THREE.MeshStandardMaterialParameters> {
    if (BODY_MESHES.has(meshFile)) {
      return { roughness: 0.45, metalness: 0.15 };
    }
    return { roughness: 0.6, metalness: 0.25 };
  }

  private refreshGroundTheme(): void {
    if (!this.ground) {
      return;
    }
    const texture = createCheckerTexture(this.theme);
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(9, 9);
    texture.anisotropy = 8;
    const material = this.ground.material as THREE.MeshStandardMaterial;
    material.map?.dispose();
    material.map = texture;
    material.needsUpdate = true;
  }

  private updateRobotMaterials(): void {
    this.bodyNodes.forEach((group) => {
      group.traverse((node) => {
        if (node instanceof THREE.Mesh && node.userData.meshFile) {
          const meshFile = node.userData.meshFile as string;
          const material = node.material as THREE.MeshStandardMaterial;
          material.color.setHex(this.getColorForMesh(meshFile));
          const overrides = this.getMaterialOverrides(meshFile);
          if (overrides.roughness !== undefined) material.roughness = overrides.roughness;
          if (overrides.metalness !== undefined) material.metalness = overrides.metalness;
          material.needsUpdate = true;
        }
      });
    });
  }

  private handleControlsChange = (): void => {
    this.needsRender = true;
  };

  private startRenderLoop(): void {
    const render = () => {
      this.controls.update();
      if (this.needsRender) {
        this.renderer.render(this.scene, this.camera);
        this.needsRender = false;
      }
      this.rafId = requestAnimationFrame(render);
    };
    this.rafId = requestAnimationFrame(render);
  }
}

function bufferToThreePosition(buffer: Float64Array, idx: number): THREE.Vector3 {
  return TEMP_POS.set(buffer[idx], buffer[idx + 1], buffer[idx + 2]).applyQuaternion(AXIS_CORRECTION);
}

function bufferToThreeQuaternion(buffer: Float64Array, idx: number): THREE.Quaternion {
  return TEMP_QUAT.set(buffer[idx + 1], buffer[idx + 2], buffer[idx + 3], buffer[idx]).premultiply(AXIS_CORRECTION);
}

function createCheckerTexture(theme: SceneTheme): THREE.CanvasTexture {
  const size = 512;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Unable to create canvas context for ground texture.');
  }
  const colors = [theme.groundDark, theme.groundLight];
  const cell = size / 8;
  for (let y = 0; y < 8; y += 1) {
    for (let x = 0; x < 8; x += 1) {
      ctx.fillStyle = colors[(x + y) % 2];
      ctx.fillRect(x * cell, y * cell, cell, cell);
    }
  }
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.06)';
  for (let i = 0; i <= size; i += cell) {
    ctx.beginPath();
    ctx.moveTo(i, 0);
    ctx.lineTo(i, size);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(0, i);
    ctx.lineTo(size, i);
    ctx.stroke();
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(9, 9);
  texture.anisotropy = 8;
  return texture;
}

function createAxisLabel(text: string, color: number): THREE.Sprite {
  const size = 128;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Unable to create axis label canvas.');
  }
  ctx.clearRect(0, 0, size, size);
  ctx.fillStyle = 'rgba(10, 17, 28, 0.9)';
  ctx.fillRect(0, 0, size, size);
  ctx.fillStyle = `#${color.toString(16).padStart(6, '0')}`;
  ctx.font = 'bold 72px Inter, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, size / 2, size / 2);

  const texture = new THREE.CanvasTexture(canvas);
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: texture, transparent: true, depthTest: false, depthWrite: false }));
  sprite.scale.set(0.18, 0.09, 1);
  return sprite;
}

function convertVector(pos: [number, number, number]): THREE.Vector3 {
  return new THREE.Vector3(pos[0], pos[1], pos[2]);
}

function convertQuaternion(quat: [number, number, number, number]): THREE.Quaternion {
  return new THREE.Quaternion(quat[1], quat[2], quat[3], quat[0]);
}
